import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { History, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRIGGER_LABELS = {
  initial_analysis: 'Initial Analysis',
  case_report_evaluation: 'Case Report Evaluation',
  manual_recalculation: 'Manual Recalculation',
  admin_reanalysis: 'Admin Re-analysis',
  pdf_reanalysis: 'PDF Re-analysis',
};

const TRIGGER_COLORS = {
  initial_analysis: 'bg-blue-100 text-blue-800',
  case_report_evaluation: 'bg-purple-100 text-purple-800',
  manual_recalculation: 'bg-amber-100 text-amber-800',
  admin_reanalysis: 'bg-orange-100 text-orange-800',
  pdf_reanalysis: 'bg-green-100 text-green-800',
};

export const ScoreHistory = ({ articleId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await fetch(`${API_URL}/api/articles/${articleId}/score-history`);
        if (res.ok) {
          const data = await res.json();
          setHistory(data.history || []);
        }
      } catch (err) {
        console.error('Failed to load score history:', err);
      }
      setLoading(false);
    };
    fetchHistory();
  }, [articleId]);

  if (loading) return null;
  if (history.length === 0) return null;

  const getDelta = (current, previous) => {
    if (!previous) return null;
    return +(current - previous).toFixed(1);
  };

  return (
    <Card data-testid="score-history-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-slate-900">
          <History className="h-5 w-5 text-indigo-600" />
          Score History
          <Badge variant="outline" className="ml-2 text-xs">{history.length} change{history.length !== 1 ? 's' : ''}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-[18px] top-2 bottom-2 w-px bg-slate-200" />

          <div className="space-y-4">
            {history.map((entry, idx) => {
              const prev = history[idx + 1];
              const delta = getDelta(entry.impact_index, prev?.impact_index);
              const isUp = delta > 0;
              const isDown = delta < 0;

              return (
                <div key={idx} className="relative flex gap-4 items-start" data-testid={`score-history-entry-${idx}`}>
                  {/* Timeline dot */}
                  <div className={`relative z-10 mt-1 w-[10px] h-[10px] rounded-full flex-shrink-0 ring-2 ring-white ${
                    idx === 0 ? 'bg-indigo-600' : 'bg-slate-300'
                  }`} style={{ marginLeft: '13px' }} />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`text-xs ${TRIGGER_COLORS[entry.trigger] || 'bg-slate-100 text-slate-700'}`}>
                        {TRIGGER_LABELS[entry.trigger] || entry.trigger}
                      </Badge>
                      <span className="text-xs text-slate-500">
                        {new Date(entry.timestamp).toLocaleDateString('bg-BG', {
                          day: 'numeric', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    </div>

                    <div className="mt-1.5 flex items-center gap-4 text-sm">
                      <span className="font-semibold text-slate-900">
                        Impact: {entry.impact_index}
                      </span>
                      <span className="text-slate-500">
                        M: {entry.methodology_score} | V: {entry.visibility_score}
                      </span>
                      {delta !== null && (
                        <span className={`flex items-center gap-0.5 text-xs font-medium ${
                          isUp ? 'text-green-600' : isDown ? 'text-red-600' : 'text-slate-400'
                        }`}>
                          {isUp && <TrendingUp className="h-3.5 w-3.5" />}
                          {isDown && <TrendingDown className="h-3.5 w-3.5" />}
                          {!isUp && !isDown && <Minus className="h-3.5 w-3.5" />}
                          {isUp && '+'}{delta}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ScoreHistory;
